<script>
  import { asSrc, asSrcset } from '$lib/Image.svelte'
  import { luma } from '$lib/utils/colors.js'
  import Button from '$lib/Button.svelte'

  /** @type {{ background?: string, overflow?: number, label?: string, image?: import('@prismicio/client').ImageField<"portrait">, link?: { href: string, text: string }, children: Svelte.Snippet }} */
  let { background, overflow, label, image, link, children } = $props()

  let isBright = $derived(background && luma(background) > 150)

  /** @type {{ srcset: string, sizes: string, media?: string }[]} */
  let sources = $derived.by(() => {
    if (!image?.url) return []

    /** @type {{ srcset: string, sizes: string, media?: string }[]}*/
    const sources = [
      {
        srcset: asSrcset(image.url, [400, 600, [800, 'q_50']]),
        sizes: '50vw',
        media: '(min-width: 700px)'
      }
    ]

    if (image?.portrait?.url) {
      sources.push({
        srcset: asSrcset(image.portrait.url, [300, 400, [600, 'q_50']]),
        sizes: '100vw'
      })
    }

    return sources
  })
</script>

<article
  class="promotion"
  style:--overflow={overflow ? overflow / 100 : null}
  style:--background={background}
  style:--rgb-text="var({isBright ? '--rgb-dark-gray' : '--rgb-white'})">
  <div class="body">
    <h2>
      {#if label}
        <small class="label">{label}</small>
      {/if}
      <div class="heading">{@render children()}</div>
    </h2>
    {#if link}
      {@const { text, ...props } = link}
      <Button cover {...props}>{text}</Button>
    {/if}
    {#if sources.length}
      <picture>
        {#each sources as attrs}
          <source {...attrs} />
        {/each}
        <img
          alt={image?.alt || ''}
          src={asSrc(image?.url, 400)}
          class="image" />
      </picture>
    {/if}
  </div>
</article>

<style>
  .promotion {
    --overflow: 0;

    display: flex;
    justify-content: center;
    padding: clamp(1.5rem, 6vw, 4rem);
    position: relative;
    z-index: 0;
    text-align: center;
    background: var(--background);
    color: rgb(var(--rgb-text));
    border-radius: var(--border-radius-lg);
  }

  .promotion::before {
    content: '';
    display: block;
    padding-top: 100%;
  }

  @media (min-width: 700px) {
    .promotion {
      display: block;
      text-align: inherit;
    }

    .promotion::before {
      content: none;
    }
  }

  .heading {
    display: block;
    margin-bottom: 1.5rem;
    font-family: var(--heading-font-family);
    font-size: clamp(1.75rem, 8vw, 3rem);
    font-weight: bold;
    line-height: 1;
    word-spacing: -0.05em;
    letter-spacing: -0.02em;
  }

  @media (min-width: 700px) {
    .heading {
      max-width: 50%;
      font-size: clamp(2rem, 5vw, 4.5rem);
    }
  }

  .label {
    display: block;
    margin-bottom: 0.75rem;
    font-size: 1rem;
  }

  .image {
    height: auto;
    width: auto;
    max-width: 100%;
    max-height: 50%;
    position: absolute;
    bottom: 0;
    left: 50%;
    z-index: -1;
    transform: translateX(-50%);
  }

  @media (min-width: 700px) {
    .image {
      height: auto;
      max-height: calc(100% * (1 + var(--overflow)));
      max-width: 60%;
      right: 0;
      left: auto;
      transform: none;
    }
  }
</style>
